import clone from 'clone';
import * as React from 'react';
import { fieldValidation } from '../../../amorg-common';
import { Button } from '../../../designSystem/button/Button';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { TextField } from '../../../designSystem/formField/text/TextField';
import { MessageCard, MessageCardVariants } from '../../../designSystem/MessageCard';
import { Small } from '../../../designSystem/typography/Body';
import { txt } from '../../../txt';

export type UserData = {
    siteId: string,
    firstname: string,
    middlename: string,
    lastname: string,
    email: string,
    password: string,
    passwordRepeated: string
};

export function getEmptyUserData() {
    return {
        siteId: '',
        firstname: '',
        middlename: '',
        lastname: '',
        email: '',
        password: '',
        passwordRepeated: ''
    } as UserData;
}

export type UserSubmissionEditorProps = {
    userData: UserData,
    message?: { type: MessageCardVariants, text: string },
    displaySite: boolean,
    disableCreateButton: boolean,
    disableValidation: boolean,
    onUserDataChange: (userData: UserData) => void,
    onCreateUser: () => void,
    onCreateUserCancel?: () => void
};

export default function UserSubmissionEditor(props: UserSubmissionEditorProps): JSX.Element {
    function cloneAndChange(mutator: (userData: UserData) => void) {
        const cloned = clone(props.userData);
        mutator(cloned);
        props.onUserDataChange(cloned);
    }

    function handleClickCreate() {
        props.onCreateUser();
    }

    function handleClickCancel() {
        props.onCreateUserCancel?.();
    }

    return (
        <Flex>
            <Flex gap={24} flexDirection="column">

                {props.message &&
                    <MessageCard variant={props.message.type}>
                        {props.message.text}
                    </MessageCard>
                }
                {props.displaySite &&

                    <TextField
                        id="organization"
                        label="Organisation"
                        value={props.userData.siteId}
                        onChange={(value) => cloneAndChange(data => data.siteId = value)}
                        validator={(value) => props.disableValidation || fieldValidation.site.id(value)
                            ? { valid: true }
                            : { valid: false, errorMessage: 'Skal udfyldes med en gyldig amorg organisation' }
                        }
                        autoComplete="off" />
                }

                <Flex flexDirection='column' gap={16}>
                    <Flex flexDirection='row' gap={16}>
                        <TextField
                            label="Fornavn"
                            value={props.userData.firstname}
                            onChange={(value) => cloneAndChange(data => data.firstname = value)}
                            validator={(value) => props.disableValidation || fieldValidation.user.firstname(value)
                                ? { valid: true }
                                : { valid: false, errorMessage: txt.fieldValidation.mandatoryText(100, value) }
                            }
                            autoComplete="off" />

                        <TextField
                            label="Mellemnavn"
                            value={props.userData.middlename}
                            onChange={(value) => cloneAndChange(data => data.middlename = value)}
                            validator={(value) => props.disableValidation || fieldValidation.user.middlename(value)
                                ? { valid: true }
                                : { valid: false, errorMessage: txt.fieldValidation.optionalText(100, value) }
                            }
                            autoComplete="off" />

                        <TextField
                            label="Efternavn"
                            value={props.userData.lastname}
                            onChange={(value) => cloneAndChange(data => data.lastname = value)}
                            validator={(value) => props.disableValidation || fieldValidation.user.lastname(value)
                                ? { valid: true }
                                : { valid: false, errorMessage: txt.fieldValidation.mandatoryText(100, value) }
                            }
                            autoComplete="off" />
                    </Flex>
                </Flex>

                <Flex flexDirection='row' gap={16}>
                    <Flex flexDirection='column' gap={8} width='50%'>
                        <TextField
                            label="E-mail"
                            value={props.userData.email}
                            onChange={(value) => cloneAndChange(data => data.email = value)}
                            validator={(value) => props.disableValidation || fieldValidation.user.email(value)
                                ? { valid: true }
                                : { valid: false, errorMessage: 'Skal være en gyldig e-mail addresse' }
                            }
                            autoComplete="off" />
                        </Flex>
                    <Flex flexDirection='column' gap={8} width='50%'>

                    </Flex>
                </Flex>
                <Flex flexDirection='row' gap={16}>
                    <Flex flexDirection='column' gap={8} width='50%'>
                        <TextField
                            type="password"
                            label="Adgangskode"
                            value={props.userData.password}
                            onChange={(value) => cloneAndChange(data => data.password = value)}
                            validator={(value) => props.disableValidation || fieldValidation.user.password(value)
                                ? { valid: true }
                                : { valid: false, errorMessage: 'Ugyldig adgangskode. Skal indeholde minimum 7 karakterer.' }
                            }
                            autoComplete="new-password" />
                        <Small>
                            Skal indeholde minimum 7 karakterer
                        </Small>
                    </Flex>
                    <Flex flexDirection='column' gap={8} width='50%'>
                        <TextField
                            id="password2"
                            type="password"
                            label="Gentag adgangskode "
                            value={props.userData.passwordRepeated}
                            onChange={(value) => cloneAndChange(data => data.passwordRepeated = value)}
                            validator={(value) => props.disableValidation || value === props.userData.password
                                ? { valid: true }
                                : { valid: false, errorMessage: 'Adgangskoderne er ikke ens.' }
                            }
                            autoComplete="new-password" />
                    </Flex>
                </Flex>
                
                <Flex flexDirection='row' justifyContent='space-between'>
                    <Button
                        variant="text"
                        onClick={handleClickCancel}
                        text={"Annullér"} />
                    <Button
                        variant="filled"
                        disabled={props.disableCreateButton}
                        onClick={handleClickCreate}
                        text={"Opret"} />
                </Flex>
            </Flex>
        </Flex>
    );
}